import {useCallback, useRef, useState} from "react";
import {uuidv7} from "uuidv7";
import {ALERT} from "../../../resources/environment";
import {resizeImage, SUPPORTED_IMAGE_MIMES} from "../../../resources/utils/imageUtils";
import {filter, map} from "underscore";

export const MAX_FILE_SIZE = 26214400; // 25 MB
const MAX_FILE_SIZE_IN_MB = MAX_FILE_SIZE / 1048576;
export const MAX_ATTACHMENTS = 5;

const useDocumentAttachments = ({onSessionCreated, uploadFn}) => {
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const ref = useRef([]);
  const inFlightRef = useRef(new Set());
  const controllersRef = useRef(new Map());

  const update = (next) => {
    ref.current = next;
    setPendingAttachments(next);
  };

  const updateEntry = (id, changes) => {
    update(
      map(ref.current, (attachment) =>
        attachment.id === id ? {...attachment, ...changes} : attachment,
      ),
    );
  };

  const handleAttach = useCallback(
    async (file, {sessionId, sessionIdRef}) => {
      if (file.size > MAX_FILE_SIZE) {
        ALERT.error(`File exceeds maximum size of ${MAX_FILE_SIZE_IN_MB}MB`);
        return;
      }

      const uploadKey = `${file.name}:${file.size}`;
      if (inFlightRef.current.has(uploadKey)) {
        return;
      }

      const id = uuidv7();
      const activeCount = filter(
        ref.current,
        (attachment) => attachment.status !== "skipped",
      ).length;
      if (activeCount >= MAX_ATTACHMENTS) {
        update([...ref.current, {id, fileName: file.name, status: "skipped"}]);
        return;
      }

      const isImage = SUPPORTED_IMAGE_MIMES.has(file.type);

      update([
        ...ref.current,
        {id, fileName: file.name, fileSize: file.size, status: "uploading", ...(isImage && {file})},
      ]);
      inFlightRef.current.add(uploadKey);

      const targetSessionId = sessionIdRef.current || sessionId || uuidv7();
      if (!sessionIdRef.current) {
        sessionIdRef.current = targetSessionId;
        onSessionCreated?.(targetSessionId);
      }

      const controller = new AbortController();
      controllersRef.current.set(id, controller);

      try {
        let uploadFile = file;
        if (isImage) {
          uploadFile = await resizeImage(file);
        }
        const result = await uploadFn(uploadFile, targetSessionId, controller.signal);
        if (sessionIdRef.current && sessionIdRef.current !== targetSessionId) {
          return;
        }
        updateEntry(id, {docId: result.docId, pageCount: result.pageCount, status: result.status});
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("Upload failed:", err);
        updateEntry(id, {status: "error", error: err.message, file: undefined});
        ALERT.error(err.message || "Upload failed");
      } finally {
        inFlightRef.current.delete(uploadKey);
        controllersRef.current.delete(id);
      }
    },
    [onSessionCreated, uploadFn],
  );

  const removeAttachment = useCallback((id) => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
    update(filter(ref.current, (a) => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    for (const controller of controllersRef.current.values()) {
      controller.abort();
    }
    controllersRef.current.clear();
    update([]);
  }, []);

  return {
    pendingAttachments,
    maxAttachments: MAX_ATTACHMENTS,
    handleAttach,
    removeAttachment,
    clearAttachments,
  };
};

export default useDocumentAttachments;
